//All of the blocks in a lesson

import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';

import { useMoveLessonBlockMutation, useUpdateLessonContentMutation } from '../lessonsApi';

import { LessonBlockComponent } from './LessonBlock';

import { LessonBlock, LessonContent as LessonContentData } from '@/shared/types/lesson';
import { SortableList, type SortableListRenderProps } from '@/shared/components/SortableList';
import { newLessonBlock } from '@/shared/utils/contentItems';
import { type ReorderPlacement } from '@/shared/utils/reorder';

interface LessonContentProps {
  content?: LessonContentData;
  lessonId: string;
  canEdit: boolean;
}

interface SortableLessonBlockProps {
  block: LessonBlock;
  canEdit: boolean;
  lessonId: string;
  onUpdate: (updatedBlock: LessonBlock) => void;
  onDelete?: () => void;
  dragProps: SortableListRenderProps;
}

const SortableLessonBlock: React.FC<SortableLessonBlockProps> = ({
  block,
  canEdit,
  lessonId,
  onUpdate,
  onDelete,
  dragProps,
}) => {
  const { setNodeRef, style, controlsDisabled, dragHandle } = dragProps;

  return (
    <Box ref={setNodeRef} style={style} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      {dragHandle && <Box sx={{ mt: 1 }}>{dragHandle}</Box>}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <LessonBlockComponent
          block={block}
          lessonId={lessonId}
          canEdit={canEdit && !controlsDisabled}
          onUpdate={onUpdate}
          onDelete={onDelete}
        />
      </Box>
    </Box>
  );
};

export const LessonContent: React.FC<LessonContentProps> = ({ content, lessonId, canEdit }) => {
  const safeContent: LessonContentData = content ?? { schemaVersion: '1' };
  const blocks: LessonBlock[] = safeContent.blocks ?? [];
  const [updateLessonContent, { isLoading: isUpdatingContent }] = useUpdateLessonContentMutation();
  const [moveLessonBlock, { isLoading: isMovingLessonBlock }] = useMoveLessonBlockMutation();

  const handleMoveBlock = async (blockId: string, placement: ReorderPlacement) => {
    await moveLessonBlock({ lessonId, blockId, placement }).unwrap();
  };

  const handleAddBlock = async () => {
    const updatedBlocks = [...blocks, newLessonBlock(blocks.length + 1)];

    try {
      await updateLessonContent({
        id: lessonId,
        content: { ...safeContent, blocks: updatedBlocks },
      }).unwrap();
    } catch (error) {
      console.error('Error adding new block:', error);
    }
  };

  const handleUpdateBlock = async (index: number, updatedBlock: LessonBlock) => {
    const updatedBlocks = [...blocks];
    updatedBlocks[index] = updatedBlock;

    try {
      await updateLessonContent({
        id: lessonId,
        content: { ...safeContent, blocks: updatedBlocks },
      }).unwrap();
    } catch (error) {
      console.error('Error updating block:', error);
    }
  };

  const handleDeleteBlock = async (index: number) => {
    const updatedBlocks = blocks
      .filter((_, i) => i !== index)
      .map((block, i) => ({
        ...block,
        lesson_order: i + 1,
      }));

    try {
      await updateLessonContent({
        id: lessonId,
        content: { ...safeContent, blocks: updatedBlocks },
      }).unwrap();
    } catch (error) {
      console.error('Error deleting block:', error);
    }
  };

  if (blocks.length === 0 && !canEdit) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          py: 4,
          color: 'text.secondary',
        }}
      >
        <Typography variant="h6">No lesson content available</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <SortableList
        items={blocks}
        canEdit={canEdit}
        isUpdating={isUpdatingContent || isMovingLessonBlock}
        onMove={handleMoveBlock}
        getItemLabel={(_block, index) => `Reorder block ${index + 1}`}
        reorderErrorMessage="Failed to reorder lesson block. Please try again."
        renderItem={(block, dragProps) => {
          const { index } = dragProps;
          return (
            <SortableLessonBlock
              block={block}
              canEdit={canEdit}
              lessonId={lessonId}
              onUpdate={(updatedBlock) => {
                void handleUpdateBlock(index, updatedBlock);
              }}
              onDelete={
                canEdit
                  ? () => {
                      void handleDeleteBlock(index);
                    }
                  : undefined
              }
              dragProps={dragProps}
            />
          );
        }}
      />

      {canEdit && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 0,
          }}
        >
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={() => {
              void handleAddBlock();
            }}
            disabled={isUpdatingContent || isMovingLessonBlock}
            sx={{ textTransform: 'none' }}
          >
            Add New Block
          </Button>
        </Box>
      )}
    </Box>
  );
};
